'use client';
import IconArrowBlackRight from '@/assets/icons/product/icon-arrow-right-slider.svg';
import ImageCard from '@/assets/images/product/image-xiomi.png';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import { Options } from '@splidejs/splide';
import Image from 'next/image';
import React, { useEffect, useRef, useState } from 'react';
import { useWindowSize } from 'react-use';
import { Button } from 'rsuite';
import styles from './styles.module.scss';
import useHasMounted from '@/hooks/useHasMounted';

interface ProductCarouselProps {
  media?:
    | {
        __typename: string;
        id: string;
        url: string;
        alt: string;
      }[]
    | null;
}

const ProductCarousel: React.FC<ProductCarouselProps> = ({ media }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [totalSlides, setTotalSlides] = useState(media?.length || 0);
  const { width } = useWindowSize();
  const hasMounted = useHasMounted();

  /**
   * The main Splide component.
   */
  const mainRef = useRef<any>(null);
  /**
   * The thumbnail Splide component.
   */
  const thumbsRef = useRef<any>(null);

  /**
   * Set the sync target after the component is mounted.
   */

  useEffect(() => {
    const thumbsSplide = thumbsRef.current?.splide;
    const mainSplide = mainRef.current?.splide;

    if (mainSplide && thumbsSplide) {
      mainSplide.sync(thumbsSplide);
      setCurrentSlide(mainSplide.index);

      const handleMoved = (newIndex: number) => {
        setCurrentSlide(newIndex);
      };

      mainSplide.on('moved', handleMoved);

      return () => {
        mainSplide.off('moved', handleMoved);
      };
    }
  }, []);

  const mainOptions: Options = {
    updateOnMove: true,
    type: 'loop',
    gap: '15px',
    perPage: 1,
    perMove: 1,
    rewind: true,
    rewindByDrag: true,
    arrows: false,
    pagination: false,
    breakpoints: {
      1200: {
        perPage: 1,
      },
      1024: {
        perPage: 1,
      },
      768: {
        perPage: 1,
        gap: '4px',
      },
      480: {
        perPage: 1,
        gap: '4px',
      },
      375: {
        // width: '256px',
        focus: 'center',
        perPage: 1,
        gap: '2px',
      },
    },
  };

  const thumbsOptions: Options = {
    type: 'slide',
    rewind: true,
    gap: '15px',
    pagination: false,
    fixedWidth: 72,
    fixedHeight: 72,
    arrows: false,
    focus: 'center',
    isNavigation: true,
    breakpoints: {
      1240: {
        width: '270px',
      },
      375: {
        width: '226px',
        gap: '2px',
      },
    },
  };

  const goToNextSlide = () => {
    const thumbsSplide = thumbsRef.current?.splide;
    if (thumbsSplide) {
      thumbsSplide.go('>');
    }
  };
  const goToPrevSlide = () => {
    const thumbsSplide = thumbsRef.current?.splide;
    if (thumbsSplide) {
      thumbsSplide.go('<');
    }
  };

  return (
    <div className={styles.wrapper}>
      <Splide
        options={mainOptions}
        ref={mainRef}
        aria-labelledby="thumbnail-slider-example"
        hasTrack={false}
        // style={{ padding: '0' }}
      >
        <SplideTrack>
          {media &&
            media.length > 0 &&
            media?.map((slide: any) => (
              <SplideSlide key={slide.id}>
                <Image
                  src={slide.url}
                  alt={slide.alt}
                  width={469}
                  height={512}
                  className={styles.mainImage}
                />
              </SplideSlide>
            ))}
        </SplideTrack>
      </Splide>

      <div className={styles.bottomSlide}>
        {hasMounted && width > 840 && (
          <Button
            type="button"
            className={styles.carouselPrevButton}
            onClick={goToPrevSlide}
            disabled={currentSlide === 0}
            style={{
              opacity: currentSlide === 0 ? 0.4 : 1,
            }}
          >
            {' '}
            <IconArrowBlackRight />
          </Button>
        )}
        <Splide
          options={thumbsOptions}
          ref={thumbsRef}
          aria-label="The carousel with thumbnails."
          hasTrack={false}
          style={{ padding: '0' }}
          className={styles.thumbsSlider}
        >
          <SplideTrack>
            {width > 840 &&
              media &&
              media.length > 0 &&
              media?.map((slide: any) => (
                <SplideSlide key={slide.id}>
                  <Image
                    src={slide.url}
                    alt={slide.alt}
                    width={50}
                    height={60}
                    className={styles.thumbnailImage}
                  />
                </SplideSlide>
              ))}
          </SplideTrack>

          {hasMounted && width <= 840 && (
            <div className={styles.btnGroup}>
              <div className={styles.customPagination}>
                {media &&
                  media.length > 0 &&
                  media?.map((_, index) => {
                    return (
                      <div
                        className={`${styles.paginationDot} ${
                          currentSlide === index ? styles.active : ''
                        }`}
                        onClick={() => mainRef.current?.splide?.go(index)}
                        key={index}
                        aria-label={`${currentSlide === index}`}
                      />
                    );
                  })}
              </div>
            </div>
          )}
        </Splide>
        {hasMounted && width > 840 && (
          <Button
            type="button"
            className={styles.carouselNextButton}
            onClick={goToNextSlide}
            disabled={currentSlide === totalSlides - 1}
            style={{
              opacity: currentSlide === totalSlides - 1 ? 0.4 : 1,
            }}
          >
            {' '}
            <IconArrowBlackRight />
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProductCarousel;
